import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { User, Lock, Mail, Bell, Mail as MailIcon, MessageSquare, Smartphone, ShieldCheck, Palette, Navigation } from 'lucide-react';
import { toast } from 'sonner';
import AppearancePanel from '../components/AppearancePanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CH = [
  { key: 'email', label: 'Email', icon: MailIcon },
  { key: 'sms', label: 'SMS', icon: MessageSquare },
  { key: 'in_app', label: 'In-App', icon: Smartphone },
];

const Profile = () => {
  const { user, checkAuth } = useAuth();
  const { canToggle: canTheme } = useTheme();
  const [name, setName] = useState(user?.name || '');
  const [phone, setPhone] = useState(user?.phone || '');
  const [savingProfile, setSavingProfile] = useState(false);

  // Preferred maps app (ask | apple | google) — saves immediately on change.
  const [mapsPref, setMapsPref] = useState(user?.maps_preference || 'ask');
  const [savingMaps, setSavingMaps] = useState(false);

  // Password
  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [savingPwd, setSavingPwd] = useState(false);

  // Email change
  const [newEmail, setNewEmail] = useState('');
  const [emailPwd, setEmailPwd] = useState('');
  const [emailStep, setEmailStep] = useState('request'); // request | verify
  const [emailCode, setEmailCode] = useState('');
  const [savingEmail, setSavingEmail] = useState(false);

  // Notification prefs
  const [prefs, setPrefs] = useState({});
  const [events, setEvents] = useState([]);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [loadingPrefs, setLoadingPrefs] = useState(true);

  useEffect(() => {
    // eslint-disable-next-line
    setName(user?.name || '');
    setPhone(user?.phone || '');
    setMapsPref(user?.maps_preference || 'ask');
  }, [user]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/auth/notification-prefs`, { withCredentials: true });
        if (!alive) return;
        setEvents(data.events || []);
        setPrefs(data.prefs || {});
      } catch (e) {
        toast.error('Failed to load notification preferences');
      } finally {
        if (alive) setLoadingPrefs(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await axios.put(`${API}/auth/profile`, { name, phone }, { withCredentials: true });
      await checkAuth();
      toast.success('Profile updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    }
    setSavingProfile(false);
  };

  const saveMapsPref = async (val) => {
    setMapsPref(val);
    setSavingMaps(true);
    try {
      await axios.put(`${API}/user/maps-preference`, { preference: val }, { withCredentials: true });
      await checkAuth();
      toast.success('Maps preference saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save maps preference');
    }
    setSavingMaps(false);
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (newPwd.length < 8) { toast.error('New password must be at least 8 characters'); return; }
    if (newPwd !== confirmPwd) { toast.error('Passwords do not match'); return; }
    setSavingPwd(true);
    try {
      await axios.post(`${API}/auth/change-password`, { current_password: currentPwd, new_password: newPwd }, { withCredentials: true });
      toast.success('Password updated');
      setCurrentPwd(''); setNewPwd(''); setConfirmPwd('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Password change failed');
    }
    setSavingPwd(false);
  };

  const requestEmailChange = async (e) => {
    e.preventDefault();
    setSavingEmail(true);
    try {
      const { data } = await axios.post(`${API}/auth/change-email`, { new_email: newEmail, current_password: emailPwd }, { withCredentials: true });
      toast.success(`Verification code sent (${data.delivery_mode === 'preview' ? 'preview' : 'email'})`);
      if (data.delivery_mode === 'preview' && data.verification_code) {
        setEmailCode(data.verification_code);
      }
      setEmailStep('verify');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start email change');
    }
    setSavingEmail(false);
  };

  const confirmEmailChange = async (e) => {
    e.preventDefault();
    setSavingEmail(true);
    try {
      const { data } = await axios.post(`${API}/auth/confirm-email-change`, { code: emailCode }, { withCredentials: true });
      toast.success(`Email updated to ${data.email}`);
      await checkAuth();
      setNewEmail(''); setEmailPwd(''); setEmailCode(''); setEmailStep('request');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Confirmation failed');
    }
    setSavingEmail(false);
  };

  const togglePref = (key, ch) => {
    if (events.find((e) => e.key === key)?.locked) return;
    setPrefs((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || {}), [ch]: !prev[key]?.[ch] },
    }));
  };

  const savePrefs = async () => {
    setSavingPrefs(true);
    try {
      const { data } = await axios.put(`${API}/auth/notification-prefs`, { prefs }, { withCredentials: true });
      setPrefs(data.prefs || {});
      toast.success('Notification preferences saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
    setSavingPrefs(false);
  };

  return (
    <div data-testid="profile-page" className="space-y-8">
      <div>
        <h2 className="font-['Outfit'] text-3xl font-bold text-white mb-2 flex items-center gap-2"><User className="w-6 h-6 text-[#D4AF37]" /> My Profile</h2>
        <p className="text-[#A3A3A3]">Manage your personal account, password, email, and notification preferences.</p>
      </div>

      <Tabs defaultValue="account" className="w-full">
        <TabsList data-testid="profile-tabs" className="bg-[#141414] border border-[#262626] flex flex-wrap h-auto">
          <TabsTrigger data-testid="tab-account" value="account" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0A] text-white">
            <User className="w-4 h-4 mr-2" /> Account
          </TabsTrigger>
          <TabsTrigger data-testid="tab-password" value="password" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0A] text-white">
            <Lock className="w-4 h-4 mr-2" /> Password
          </TabsTrigger>
          <TabsTrigger data-testid="tab-email" value="email" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0A] text-white">
            <Mail className="w-4 h-4 mr-2" /> Email
          </TabsTrigger>
          <TabsTrigger data-testid="tab-notifications" value="notifications" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0A] text-white">
            <Bell className="w-4 h-4 mr-2" /> Notifications
          </TabsTrigger>
          {canTheme && (
            <TabsTrigger data-testid="tab-appearance" value="appearance" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0A] text-white">
              <Palette className="w-4 h-4 mr-2" /> Appearance
            </TabsTrigger>
          )}
        </TabsList>

        {/* Account tab */}
        <TabsContent value="account" className="mt-6">
          <form onSubmit={saveProfile} className="bg-[#141414] border border-[#262626] rounded-md p-6 space-y-4 max-w-2xl">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-white">Full Name</Label>
                <Input data-testid="profile-name" required value={name} onChange={(e) => setName(e.target.value)} className="bg-[#0A0A0A] border-[#262626] text-white" />
              </div>
              <div>
                <Label className="text-white">Cell Phone</Label>
                <Input data-testid="profile-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(555) 555-5555" className="bg-[#0A0A0A] border-[#262626] text-white" />
                <p className="text-[#525252] text-xs mt-1">Used for SMS reminders, appointment updates, and security alerts.</p>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-white">Email</Label>
                <Input value={user?.email || ''} readOnly className="bg-[#0A0A0A] border-[#262626] text-[#A3A3A3]" />
                <p className="text-[#525252] text-xs mt-1">To change your email, use the Email tab.</p>
              </div>
              <div>
                <Label className="text-white">Role</Label>
                <Input value={(user?.role || '').toUpperCase()} readOnly className="bg-[#0A0A0A] border-[#262626] text-[#A3A3A3]" />
              </div>
            </div>
            <div>
              <Label className="text-white flex items-center gap-2"><Navigation className="w-4 h-4 text-[#D4AF37]" /> Preferred Maps App</Label>
              <Select value={mapsPref} onValueChange={saveMapsPref} disabled={savingMaps}>
                <SelectTrigger data-testid="maps-pref-select" className="bg-[#0A0A0A] border-[#262626] text-white max-w-xs mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#141414] border-[#262626] text-white">
                  <SelectItem value="ask" data-testid="maps-pref-ask">Ask Every Time</SelectItem>
                  <SelectItem value="apple" data-testid="maps-pref-apple">Apple Maps</SelectItem>
                  <SelectItem value="google" data-testid="maps-pref-google">Google Maps</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[#525252] text-xs mt-1">Used when you tap a service address for directions. &quot;Ask Every Time&quot; lets you choose on each tap.</p>
            </div>
            <Button data-testid="save-profile" type="submit" disabled={savingProfile} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]">
              {savingProfile ? 'Saving…' : 'Save Profile'}
            </Button>
          </form>
        </TabsContent>

        {/* Password tab */}
        <TabsContent value="password" className="mt-6">
          <form onSubmit={changePassword} className="bg-[#141414] border border-[#262626] rounded-md p-6 space-y-4 max-w-md">
            <div>
              <Label className="text-white">Current Password</Label>
              <Input data-testid="current-password" type="password" required value={currentPwd} onChange={(e) => setCurrentPwd(e.target.value)} className="bg-[#0A0A0A] border-[#262626] text-white" />
            </div>
            <div>
              <Label className="text-white">New Password</Label>
              <Input data-testid="new-password" type="password" minLength={8} required value={newPwd} onChange={(e) => setNewPwd(e.target.value)} className="bg-[#0A0A0A] border-[#262626] text-white" />
            </div>
            <div>
              <Label className="text-white">Confirm New Password</Label>
              <Input data-testid="confirm-password" type="password" minLength={8} required value={confirmPwd} onChange={(e) => setConfirmPwd(e.target.value)} className="bg-[#0A0A0A] border-[#262626] text-white" />
            </div>
            <div className="flex items-start gap-2 text-[#525252] text-xs">
              <ShieldCheck className="w-3.5 h-3.5 mt-0.5" />
              <p>Min 8 characters. We&apos;ll log you out from other sessions on next sign-in.</p>
            </div>
            <Button data-testid="save-password" type="submit" disabled={savingPwd} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]">
              {savingPwd ? 'Updating…' : 'Update Password'}
            </Button>
          </form>
        </TabsContent>

        {/* Email tab */}
        <TabsContent value="email" className="mt-6">
          <div className="bg-[#141414] border border-[#262626] rounded-md p-6 space-y-4 max-w-md">
            {emailStep === 'request' ? (
              <form onSubmit={requestEmailChange} className="space-y-4">
                <p className="text-[#A3A3A3] text-sm">Current email: <span className="text-white">{user?.email}</span></p>
                <div>
                  <Label className="text-white">New Email</Label>
                  <Input data-testid="new-email" type="email" required value={newEmail} onChange={(e) => setNewEmail(e.target.value)} className="bg-[#0A0A0A] border-[#262626] text-white" />
                </div>
                <div>
                  <Label className="text-white">Current Password</Label>
                  <Input data-testid="email-current-pwd" type="password" required value={emailPwd} onChange={(e) => setEmailPwd(e.target.value)} className="bg-[#0A0A0A] border-[#262626] text-white" />
                </div>
                <p className="text-[#525252] text-xs">We&apos;ll send a 6-digit code to the new address to confirm you own it.</p>
                <Button data-testid="request-email-change" type="submit" disabled={savingEmail} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]">
                  {savingEmail ? 'Sending…' : 'Send Verification Code'}
                </Button>
              </form>
            ) : (
              <form onSubmit={confirmEmailChange} className="space-y-4">
                <p className="text-[#A3A3A3] text-sm">A 6-digit code was sent to <span className="text-white">{newEmail}</span>. Enter it below to confirm.</p>
                <div>
                  <Label className="text-white">Verification Code</Label>
                  <Input data-testid="email-change-code" inputMode="numeric" maxLength={6} required value={emailCode} onChange={(e) => setEmailCode(e.target.value)} className="bg-[#0A0A0A] border-[#262626] text-white tracking-[0.5em] text-center text-xl" />
                </div>
                <div className="flex gap-2">
                  <Button data-testid="confirm-email-change" type="submit" disabled={savingEmail} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]">
                    {savingEmail ? 'Confirming…' : 'Confirm Email Change'}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => { setEmailStep('request'); setEmailCode(''); }} className="border-[#262626] text-white hover:bg-[#262626]">Start Over</Button>
                </div>
              </form>
            )}
          </div>
        </TabsContent>

        {/* Notifications tab */}
        <TabsContent value="notifications" className="mt-6">
          <div className="bg-[#141414] border border-[#262626] rounded-md p-6 space-y-4">
            <p className="text-[#A3A3A3] text-sm">Choose how you&apos;d like to hear from us for each event type. Security alerts cannot be disabled.</p>
            {loadingPrefs ? (
              <p className="text-[#A3A3A3]">Loading…</p>
            ) : (
              <div className="grid gap-3">
                {events.map((ev) => {
                  const cfg = prefs[ev.key] || { email: false, sms: false, in_app: false };
                  const locked = !!ev.locked;
                  return (
                    <div key={ev.key} data-testid={`pref-row-${ev.key}`} className="flex items-center justify-between gap-4 flex-wrap p-3 rounded-md hover:bg-[#1a1a1a]">
                      <div>
                        <p className="text-white text-sm">{ev.label}</p>
                        {locked && <p className="text-[#525252] text-xs">Always on for account security.</p>}
                      </div>
                      <div className="flex gap-2">
                        {CH.map((c) => {
                          const Icon = c.icon;
                          const on = !!cfg[c.key];
                          return (
                            <button
                              key={c.key}
                              type="button"
                              disabled={locked}
                              data-testid={`pref-${ev.key}-${c.key}`}
                              onClick={() => togglePref(ev.key, c.key)}
                              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-xs transition-colors ${
                                on
                                  ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]'
                                  : 'border-[#262626] text-[#A3A3A3] hover:text-white'
                              } ${locked ? 'opacity-60 cursor-not-allowed' : ''}`}
                            >
                              <Icon className="w-3.5 h-3.5" /> {c.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <Button data-testid="save-prefs" onClick={savePrefs} disabled={savingPrefs} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]">
              {savingPrefs ? 'Saving…' : 'Save Preferences'}
            </Button>
          </div>
        </TabsContent>

        {canTheme && (
          <TabsContent value="appearance" className="mt-6">
            <AppearancePanel />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

export default Profile;
